import { useMemo, useState } from 'react'
import {
  Box, Card, Typography, Checkbox, TextField, InputAdornment, ToggleButtonGroup, ToggleButton, Chip, Tooltip,
  FormControl, Select, MenuItem, InputLabel, Button, Snackbar, TableSortLabel, CircularProgress,
} from '@mui/material'
import { Search, WarningAmber } from '@mui/icons-material'
import { footerTotals, dosStatus } from './fourToTwelve'
import {
  visibleRows, sortRows, bulkTargets, ACTIONS, scopeMonths, keptEditCount, bulkRule, appliedMessage,
  reviewTooltip, monthHeading, demandChange, abbreviate, selectAllState,
} from './nextThreeMonths'
import { BASES, PCT_STEPS } from './fourToTwelve'
import UnitsCell from './UnitsCell'
import { frozenWidths, tableWidth, COL_W } from './gridLayout'

const GREEN = '#15803D'
const TINT = '#F0F7FF' // §2 alternate months, light blue
const num = (v) => (v == null ? '—' : Math.round(v).toLocaleString())
const dosColor = { below: '#DC2626', above: '#D97706', ok: 'inherit', none: 'inherit' }
const SUB = ['DEMAND', 'UNITS', 'INVENTORY', 'DOS']

// FAR-77: the Next 3 Months tab's SKU Plan. As FAR-76's 4–18 card, plus search,
// Brite / Line / Rate/hr, four values a month (DEMAND, UNITS, INVENTORY, DOS),
// the always-visible bulk bar, and the §5.3 review indicator (an icon, not a
// banner). Line and Rate/hr are the primary line's, read-only: moving a SKU to
// another line waits for the other lines' run rates (Mark, 2026-10-20).
export default function NextThreeMonthsCard({
  rows, months, basePlan, line, onLineChange, lines, baseRowsByItem, modifications,
  selected, onSelect, editCounts, onEditUnits, onRevertSku, onApplyRule, review, onBackToBasePlan, readOnly,
  withLines = true, testId = 'next3', forecastChanges, modificationName, scrollRef,
}) {
  // FAR-83: the Months 4–18 tab uses this card without its line features (no
  // Line or Rate/hr column, no Line filter). Planning there is by SKU.
  // FAR-83 #13: fixed widths shared with Capacity and Risk (gridLayout.js), so
  // the months line up. The <colgroup> sets them; cells can't grow.
  const W = frozenWidths(withLines)
  const leftOf = (i) => W.slice(0, i).reduce((a, b) => a + b, 0)
  const stick = (i, extra = {}) => ({
    position: 'sticky', left: leftOf(i), zIndex: 2, bgcolor: 'background.paper', ...extra,
  })
  // Frozen headers at 0.75rem: at the inherited 1rem, "Rate/hr" plus the sort
  // icon doesn't fit in 70px.
  // (Their z-index is set on the header row; see data-frozen.)
  const headSx = (i, extra = {}) => stick(i, { fontSize: '0.75rem', ...extra })
  const frozen = W.length
  const [status, setStatus] = useState('all')
  const [query, setQuery] = useState('')
  const [sort, setSort] = useState({ key: '', dir: 'asc' })
  const [action, setAction] = useState('increase')
  const [scope, setScope] = useState('all')
  const [pct, setPct] = useState(10)
  const [basis, setBasis] = useState('Min')
  const [busy, setBusy] = useState(false)
  const [toast, setToast] = useState('')

  const shown = useMemo(
    () => sortRows(visibleRows(rows, months, { line: withLines ? line : '', status, query }), sort.key, sort.dir),
    [rows, months, line, status, query, sort, withLines],
  )
  const totals = useMemo(() => footerTotals(rows, months, baseRowsByItem), [rows, months, baseRowsByItem])
  const targets = useMemo(() => bulkTargets(shown, selected), [shown, selected])
  const selectable = shown.filter((r) => !r.isMadeToOrder)
  const checkState = selectAllState(selectable, selected) // FAR-83 §4.2: none / some / all

  const pctOptions = action === 'increase' ? [-50, -25, -20, -10, -5, 5, 10, 15, 20, 25, 50, 100] : PCT_STEPS
  const setActionKeepingPct = (a) => {
    setAction(a)
    setPct(a === 'increase' ? 10 : 100)
  }

  const apply = async () => {
    if (!targets.length || busy) return
    const kept = keptEditCount(modifications, targets, scopeMonths(months, scope))
    setBusy(true)
    try {
      const ok = await onApplyRule(bulkRule({ action, pct, basis, items: targets, months, scope }))
      if (ok !== false) {
        setToast(appliedMessage(targets.length, kept, modificationName))
        onSelect?.(targets, false)
      }
    } finally {
      setBusy(false)
    }
  }

  const sortBy = (key) => setSort((s) => ({ key, dir: s.key === key && s.dir === 'asc' ? 'desc' : 'asc' }))
  const head = (key, text, align = 'center') => (
    <TableSortLabel active={sort.key === key} direction={sort.key === key ? sort.dir : 'asc'} onClick={() => sortBy(key)}
      sx={{ justifyContent: align === 'left' ? 'flex-start' : 'center' }}>{text}</TableSortLabel>
  )
  const cellSx = { textAlign: 'center', px: 0.5, fontSize: '0.8rem' }
  const tint = (i) => (i % 2 === 1 ? { bgcolor: TINT } : {})

  return (
    <Card sx={{ borderLeft: `4px solid ${GREEN}`, boxShadow: 3 }} data-testid={`${testId}-card`}>
      {/* §5.3 header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, pt: 1, flexWrap: 'wrap' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }} data-testid={`${testId}-count`}>
          SKU Plan · {shown.length} of {rows.length}
        </Typography>
        {review.length > 0 && (
          <>
            <Tooltip title={reviewTooltip(review.length)}>
              <WarningAmber sx={{ color: '#D97706' }} data-testid={`${testId}-review-icon`} aria-label={reviewTooltip(review.length)} />
            </Tooltip>
            {!readOnly && (
              <Button size="small" variant="contained" onClick={onBackToBasePlan} data-testid={`${testId}-back-to-base`}
                sx={{ bgcolor: GREEN, '&:hover': { bgcolor: '#166534' } }}>Back to Base Plan</Button>
            )}
          </>
        )}
        <Box sx={{ flex: 1 }} />
        <TextField size="small" placeholder="Search description, item # or Brite" value={query} onChange={(e) => setQuery(e.target.value)}
          inputProps={{ 'aria-label': 'Search SKUs' }} sx={{ width: 280 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />
        <ToggleButtonGroup size="small" exclusive value={status} onChange={(_, v) => v && setStatus(v)} aria-label="DOS status">
          <ToggleButton value="all">All SKUs</ToggleButton>
          <ToggleButton value="below">Below Min DOS</ToggleButton>
          <ToggleButton value="above">Above Max DOS</ToggleButton>
        </ToggleButtonGroup>
        {withLines && (
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel id={`${testId}-line`}>Line</InputLabel>
            <Select labelId={`${testId}-line`} label="Line" value={line} onChange={(e) => onLineChange(e.target.value)}>
              <MenuItem value="">All Lines</MenuItem>
              {lines.map((l) => <MenuItem key={l} value={l}>{l.replace('LINE', 'Line')}</MenuItem>)}
            </Select>
          </FormControl>
        )}
      </Box>

      {/* §5.6 bulk bar, always visible */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, flexWrap: 'wrap' }} data-testid={`${testId}-bulk-bar`}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>Modify Selected</Typography>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <Select value={action} onChange={(e) => setActionKeepingPct(e.target.value)} inputProps={{ 'aria-label': 'Action' }}>
            {ACTIONS.map((a) => <MenuItem key={a.key} value={a.key}>{a.label}</MenuItem>)}
          </Select>
        </FormControl>
        <Typography variant="body2">in</Typography>
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <Select value={scope} onChange={(e) => setScope(e.target.value)} inputProps={{ 'aria-label': 'Months' }}>
            <MenuItem value="all">All months</MenuItem>
            {months.map((m) => <MenuItem key={m.iso} value={m.iso}>{m.label}</MenuItem>)}
          </Select>
        </FormControl>
        <Typography variant="body2">by</Typography>
        <FormControl size="small" sx={{ minWidth: 90 }}>
          <Select value={pct} onChange={(e) => setPct(e.target.value)} inputProps={{ 'aria-label': 'Percent' }}>
            {pctOptions.map((p) => <MenuItem key={p} value={p}>{action === 'increase' && p > 0 ? `+${p}` : p}%</MenuItem>)}
          </Select>
        </FormControl>
        {action === 'dos' && (
          <>
            <Typography variant="body2">of</Typography>
            <FormControl size="small" sx={{ minWidth: 110 }}>
              <Select value={basis} onChange={(e) => setBasis(e.target.value)} inputProps={{ 'aria-label': 'DOS basis' }}>
                {BASES.map((b) => <MenuItem key={b} value={b}>{b} DOS</MenuItem>)}
              </Select>
            </FormControl>
          </>
        )}
        <Button size="small" variant="contained" disabled={readOnly || targets.length === 0 || busy} onClick={apply}
          data-testid={`${testId}-apply`} startIcon={busy ? <CircularProgress size={14} color="inherit" /> : null}
          sx={{ bgcolor: GREEN, '&:hover': { bgcolor: '#166534' } }}>Apply</Button>
        <Typography variant="caption" color="text.secondary">
          {targets.length ? `${targets.length} selected` : 'Check SKUs to modify'} · typed cells are kept
        </Typography>
      </Box>

      {/* Scrolls sideways with Capacity and Risk (useSyncedScrollX). */}
      <Box ref={scrollRef} sx={{ overflow: 'auto', maxHeight: '60vh', scrollbarGutter: 'stable' }}>
        <Box component="table" sx={{
          borderCollapse: 'separate', borderSpacing: 0, tableLayout: 'fixed', width: tableWidth(withLines, months.length),
          '& td, & th': { borderBottom: '1px solid', borderColor: 'divider', py: 0.5, overflow: 'hidden' },
        }}>
          <colgroup>
            {W.map((w, i) => <col key={`f${i}`} style={{ width: w }} />)}
            {months.map((m) => SUB.map((s) => <col key={`${m.key}${s}`} style={{ width: COL_W }} />))}
          </colgroup>
          <thead>
            {/* Frozen headers sit above the month headers that scroll under
                them. Set here, not in each cell's sx: this row's `& th` rule
                is more specific than a cell's own class and would win. */}
            <Box component="tr" sx={{
              '& th': { position: 'sticky', top: 0, zIndex: 3, bgcolor: 'background.paper' },
              '& th[data-frozen]': { zIndex: 4 },
            }}>
              <Box component="th" data-frozen rowSpan={2} sx={{ ...headSx(0), px: 0 }}>
                <Checkbox size="small" checked={checkState === 'all'} indeterminate={checkState === 'some'} disabled={readOnly}
                  inputProps={{ 'aria-label': 'Select all shown' }}
                  // some -> clears, like none -> all: a click on a part-checked box empties it.
                  onChange={() => onSelect?.(selectable.map((r) => r.itemNumber), checkState === 'none')} />
              </Box>
              <Box component="th" data-frozen rowSpan={2} sx={headSx(1, { textAlign: 'left' })}>{head('description', 'Item', 'left')}</Box>
              <Box component="th" data-frozen rowSpan={2} sx={headSx(2)}>{head('briteCode', 'Brite')}</Box>
              {withLines && <Box component="th" data-frozen rowSpan={2} sx={headSx(3)}>{head('line', 'Line')}</Box>}
              <Box component="th" data-frozen rowSpan={2} sx={headSx(withLines ? 4 : 3)}>{head('dosMin', 'DOS')}</Box>
              {withLines && <Box component="th" data-frozen rowSpan={2} sx={headSx(5)}>{head('runRateCasesPerHour', 'Rate/hr')}</Box>}
              {months.map((m, i) => (
                <Box component="th" key={m.key} colSpan={4} sx={{ borderLeft: '2px solid', borderColor: 'divider', fontSize: '0.8rem', ...tint(i) }}>
                  {monthHeading(m, i, basePlan)}
                </Box>
              ))}
            </Box>
            <Box component="tr" sx={{ '& th': { position: 'sticky', top: 33, zIndex: 3, bgcolor: 'background.paper', fontSize: '0.65rem', color: 'text.secondary' } }}>
              {months.map((m, i) => SUB.map((s, j) => (
                <Box component="th" key={`${m.key}${s}`} sx={{ ...cellSx, ...(j === 0 ? { borderLeft: '2px solid', borderColor: 'divider' } : {}), ...tint(i) }}>{s}</Box>
              )))}
            </Box>
          </thead>
          <tbody>
            {shown.map((r) => (
              <Box component="tr" key={r.itemNumber} data-testid={`${testId}-row-${r.itemNumber}`}>
                <Box component="td" sx={{ ...stick(0), px: 0 }}>
                  <Checkbox size="small" checked={!!selected?.has(r.itemNumber)} disabled={readOnly || r.isMadeToOrder}
                    inputProps={{ 'aria-label': `Select ${r.itemNumber}` }}
                    onChange={(e) => onSelect?.([r.itemNumber], e.target.checked)} />
                </Box>
                <Box component="td" sx={{ ...stick(1), textAlign: 'left', px: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', lineHeight: 1.2 }}>{r.description}</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                    <Typography variant="caption" color="primary">{r.itemNumber}</Typography>
                    <Typography variant="caption" color="text.secondary">· {r.baseUom || 'CS'}</Typography>
                    {r.isMadeToOrder && <Chip size="small" label="Made to order" sx={{ height: 16, fontSize: '0.6rem' }} />}
                    {editCounts?.[r.itemNumber] > 0 && (
                      <Tooltip title={readOnly ? '' : 'Revert this SKU to the Base Plan'}>
                        <Chip size="small" label={`${editCounts[r.itemNumber]} edit${editCounts[r.itemNumber] === 1 ? '' : 's'}`}
                          onDelete={readOnly ? undefined : () => onRevertSku?.(r.itemNumber)}
                          sx={{ height: 16, fontSize: '0.6rem', bgcolor: '#DCFCE7', color: GREEN, '& .MuiChip-deleteIcon': { fontSize: 13, color: GREEN } }} />
                      </Tooltip>
                    )}
                  </Box>
                </Box>
                <Box component="td" sx={{ ...stick(2), textAlign: 'center', fontSize: '0.75rem' }}>{r.briteCode || '—'}</Box>
                {withLines && (
                  <Box component="td" sx={{ ...stick(3), textAlign: 'center' }}>
                    <Tooltip title="Primary line. Moving to another line comes with its run rates.">
                      <Chip size="small" label={(r.line || '—').replace('LINE ', 'L')} sx={{ height: 18, fontSize: '0.7rem' }} />
                    </Tooltip>
                  </Box>
                )}
                <Box component="td" sx={{ ...stick(withLines ? 4 : 3), textAlign: 'center', fontSize: '0.75rem' }}>
                  {r.dosMin ? (<span>{r.dosMin} <Box component="span" sx={{ color: GREEN, fontWeight: 700 }}>{r.dosTarget || '–'}</Box> {r.dosMax}</span>) : '—'}
                </Box>
                {withLines && <Box component="td" sx={{ ...stick(5), textAlign: 'center', fontSize: '0.75rem' }}>{r.runRateCasesPerHour ? num(r.runRateCasesPerHour) : '—'}</Box>}
                {months.map((m, i) => {
                  const dos = r[`${m.key}_dos`]
                  const st = dosStatus(r, dos, m.key)
                  const t = tint(i)
                  return [
                    (() => {
                      // Blue when the forecast moved since the Base Plan (FAR-77 §5.5).
                      const ch = demandChange(forecastChanges, r.itemNumber, m.iso)
                      const cell = (
                        <Box component="td" key={`${m.key}d`} data-demand-changed={ch ? 'true' : undefined}
                          sx={{ ...cellSx, ...t, borderLeft: '2px solid', borderColor: 'divider', ...(ch ? { color: '#2563EB', fontWeight: 700 } : {}) }}>
                          {num(r[`${m.key}_fcst`])}
                        </Box>
                      )
                      return ch ? <Tooltip key={`${m.key}d`} title={ch.tooltip}>{cell}</Tooltip> : cell
                    })(),
                    // §5.3: no amber marker on this tab; the header icon flags review.
                    <UnitsCell key={`${m.key}u`} label={`${r.itemNumber} ${m.label}`} units={r[`${m.key}_cases`]}
                      baseUnits={baseRowsByItem?.[r.itemNumber]?.[`${m.key}_cases`]} edited={!!r[`${m.key}_cellEdited`]}
                      roundingValue={r.roundingValue} disabled={readOnly || !onEditUnits}
                      onCommit={(v) => onEditUnits(r, m, v)} sx={{ ...cellSx, ...t }} />,
                    <Box component="td" key={`${m.key}i`} sx={{ ...cellSx, ...t, color: 'text.secondary' }}>{num(r[`${m.key}_endInv`])}</Box>,
                    <Box component="td" key={`${m.key}s`} data-dos-status={st}
                      sx={{ ...cellSx, ...t, color: dosColor[st], fontWeight: st === 'below' || st === 'above' ? 700 : 400 }}>
                      {dos == null ? '—' : Math.round(dos)}
                      {st === 'below' && <Box component="span" aria-label="below Min" sx={{ ml: 0.25, fontSize: '0.6rem' }}>▼</Box>}
                      {st === 'above' && <Box component="span" aria-label="above Max" sx={{ ml: 0.25, fontSize: '0.6rem' }}>▲</Box>}
                    </Box>,
                  ]
                })}
              </Box>
            ))}
          </tbody>
          <tfoot>
            {[
              ['Total', (t) => [abbreviate(t.demand), abbreviate(t.units), abbreviate(t.inventory), t.avgDos == null ? '—' : Math.round(t.avgDos)], {}],
              ['Plan vs Demand', (t) => ['', abbreviate(t.cumPlanVsDemand), t.cumPlanVsDemandPct == null ? '—' : `${Math.round(t.cumPlanVsDemandPct)}%`, ''], { color: 'text.secondary' }],
              ['Δ vs Base Plan', (t) => ['', abbreviate(t.deltaVsBase), t.deltaVsBasePct == null ? '—' : `${Math.round(t.deltaVsBasePct)}%`, ''], { delta: true }],
            ].map(([label, fn, opt]) => (
              <Box component="tr" key={label} sx={{ '& td': { bgcolor: 'grey.50', fontWeight: 700 } }}>
                <Box component="td" colSpan={frozen} sx={{ ...stick(0), textAlign: 'left', px: 1 }}>
                  {label === 'Total' ? `Total · all ${rows.length} SKUs` : label === 'Plan vs Demand' ? 'Plan vs Demand · cumulative' : label}
                  {label === 'Total' && totals.excludedPallet > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 400 }}>
                      Cases only · {totals.excludedPallet} pallet item{totals.excludedPallet === 1 ? '' : 's'} not included
                    </Typography>
                  )}
                </Box>
                {months.map((m) => {
                  const t = totals.perMonth[m.key] || {}
                  const color = opt.delta && t.deltaVsBase ? GREEN : (opt.color || 'inherit')
                  return fn(t).map((v, i) => (
                    <Box component="td" key={`${m.key}${i}`} sx={{ ...cellSx, color, borderLeft: i === 0 ? '2px solid' : undefined, borderColor: 'divider' }}>{v}</Box>
                  ))
                })}
              </Box>
            ))}
          </tfoot>
        </Box>
      </Box>
      <Snackbar open={!!toast} autoHideDuration={5000} onClose={(_e, reason) => { if (reason !== 'clickaway') setToast('') }}
        message={toast} data-testid={`${testId}-toast`} />
    </Card>
  )
}
